import React, { useState, useMemo } from 'react';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import ConfirmDialog from '../shared/ConfirmDialog';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';

const ManualSlotSelectionModal = ({
    isOpen,
    onClose,
    onSelect,
    factories = [],
    doorGroups = [],
    docks = [],
    reservations = [],
    initialSelection,
    selectedDate,
    onDateChange,
    duration = 30, // Default 30 min if not passed, though should be 0 if enforced
    slotDuration = 30, // System Config: 30, 20, 15 etc.
    dockTypes = [],
    vehicleTypeCode = null
}) => {
    // Local state for the form within the modal
    const { confirmConfig, showConfirm, closeConfirm } = useConfirmDialog();
    const [selection, setSelection] = useState(initialSelection || {
        factoryId: '',
        doorGroupId: '',
        dockId: '',
        selectedSlotTime: null
    });

    const [filterAvailable, setFilterAvailable] = useState(false); // Default: Show all
    const [currentPage, setCurrentPage] = useState(0);
    const SLOTS_PER_PAGE = 20;

    // Reset selection when modal opens/closes or initialSelection changes
    // (In a real app, we might want to sync this better, but for now we init from props)

    // Derived Docks based on selection
    const filteredDoorGroups = useMemo(() => {
        if (!selection.factoryId) return [];
        return doorGroups.filter(dg => dg.factoryId === selection.factoryId);
    }, [doorGroups, selection.factoryId]);

    const filteredDocks = useMemo(() => {
        if (!selection.doorGroupId) return [];
        let list = docks.filter(d => d.doorGroupId === selection.doorGroupId);

        // Filter by compatibility if vehicle type is specified
        if (vehicleTypeCode && dockTypes.length > 0) {
            list = list.filter(dock => {
                const dockTypeCfg = dockTypes.find(dt => dt.code === dock.type);
                if (!dockTypeCfg) return true;
                if (!dockTypeCfg.allowedVehicleTypes || dockTypeCfg.allowedVehicleTypes.length === 0) return true;
                return dockTypeCfg.allowedVehicleTypes.includes(vehicleTypeCode);
            });
        }

        return list;
    }, [docks, selection.doorGroupId, vehicleTypeCode, dockTypes]);

    // Generate Slots Logic
    const slots = useMemo(() => {
        if (!selection.dockId || !selectedDate) return [];

        const generatedSlots = [];
        const startHour = 8;
        const endHour = 24;

        // Determine the effective start hour based on the selected date
        let displayStartHour = startHour;
        const today = new Date();
        const selDate = new Date(selectedDate);

        // Check if selected date is "today"
        // Note: selectedDate string format is usually YYYY-MM-DD
        // We need to compare just the date parts.
        const isToday = selDate.toDateString() === today.toDateString();

        if (isToday) {
            // If today, start showing slots from the NEXT hour
            // e.g., if now is 16:56, we want to start showing from 17:00
            const currentHour = today.getHours();
            // Ensure we don't start earlier than the configured filtering start hour (e.g. 08:00)
            // But if it's already evening (e.g. 20:00), we start from 21:00.
            // Start from currentHour + 1 to show the immediate next slot (e.g. 17:00 if now is 16:56)
            displayStartHour = Math.max(startHour, currentHour + 1);
        }

        // Helper to check collision
        const checkOccupancy = (h, m) => {
            // Even if duration is 0, we treat it as minimum 1 minute to check point-in-time collision
            const checkDuration = duration > 0 ? duration : slotDuration;

            const slotStart = new Date(selectedDate);
            slotStart.setHours(h, m, 0, 0);

            const slotEnd = new Date(slotStart);
            slotEnd.setMinutes(slotEnd.getMinutes() + checkDuration);

            // Check if this NEW slot (start->end) overlaps with any EXISTING reservation
            // Overlap formula: (StartA < EndB) and (EndA > StartB)
            const collision = reservations.find(res => {
                if (res.dockId !== selection.dockId) return false;
                const resStart = new Date(res.startTime);
                const resEnd = new Date(res.endTime);

                // Use strictly less/greater to allow abutment (e.g. one ends at 10:00, next starts at 10:00)
                // But generally overlap is: max(start1, start2) < min(end1, end2)
                return (slotStart < resEnd && slotEnd > resStart);
            });

            if (collision) {
                return {
                    isOccupied: true,
                    details: `${collision.transporterName || 'Bilinmeyen Araç'} (${new Date(collision.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}-${new Date(collision.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`
                };
            }
            return { isOccupied: false };
        };

        for (let i = displayStartHour; i < endHour; i++) {
            // Logic to support dynamic minute steps
            for (let min = 0; min < 60; min += slotDuration) {
                const hourStr = i.toString().padStart(2, '0');
                const minStr = min.toString().padStart(2, '0');

                const timeLabel = `${hourStr}:${minStr}`;
                const status = checkOccupancy(i, min);

                // FILTER LOGIC: If checkbox is active AND slot is occupied or causes overlap, SKIP IT
                if (filterAvailable && status.isOccupied) continue;

                generatedSlots.push({
                    timeLabel,
                    isOccupied: status.isOccupied,
                    occupiedDetails: status.details,
                    hour: i,
                    minute: min
                });
            }
        }
        return generatedSlots;
    }, [selection.dockId, selectedDate, reservations, filterAvailable, duration, slotDuration]);

    // Pagination Logic
    const totalPages = Math.ceil(slots.length / SLOTS_PER_PAGE);
    const currentSlots = slots.slice(currentPage * SLOTS_PER_PAGE, (currentPage + 1) * SLOTS_PER_PAGE);

    const handleSelect = () => {
        onSelect(selection);
        onClose();
    };

    const handleSlotClick = (slot) => {
        if (slot.isOccupied) {
            showConfirm({
                title: 'Çakışan Rezervasyon',
                message: `Seçtiğiniz slot (${slot.timeLabel}) dolu. Çakışan Rezervasyon: ${slot.occupiedDetails}. Yine de bu slotu seçmek ve rezervasyonu üst üste kaydetmek istiyor musunuz?`,
                type: 'warning',
                confirmText: 'Evet, Seç',
                onConfirm: () => {
                    setSelection(prev => ({ ...prev, selectedSlotTime: slot.timeLabel }));
                }
            });
            return;
        }
        setSelection(prev => ({ ...prev, selectedSlotTime: slot.timeLabel }));
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Manuel Slot Seçimi"
            size="lg" // Larger modal for better grid view
        >
            <div className="flex flex-col gap-md h-full">
                {/* 1. Configuration Row: Factory -> DoorGroup -> Dock */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-lg p-lg rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                    <div className="form-group mb-0">
                        <label className="text-xs font-semibold uppercase tracking-wider mb-2 block pl-1" style={{ color: 'var(--text-secondary)' }}>Fabrika</label>
                        <select
                            className="input w-full"
                            value={selection.factoryId}
                            onChange={e => setSelection({ ...selection, factoryId: e.target.value, doorGroupId: '', dockId: '', selectedSlotTime: null })}
                        >
                            <option value="">Seçiniz</option>
                            {factories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                    </div>

                    <div className="form-group mb-0">
                        <label className="text-xs font-semibold uppercase tracking-wider mb-2 block pl-1" style={{ color: 'var(--text-secondary)' }}>Rampa Grubu</label>
                        <select
                            className="input w-full"
                            value={selection.doorGroupId}
                            disabled={!selection.factoryId}
                            onChange={e => setSelection({ ...selection, doorGroupId: e.target.value, dockId: '', selectedSlotTime: null })}
                        >
                            <option value="">Seçiniz</option>
                            {filteredDoorGroups.map(dg => <option key={dg.id} value={dg.id}>{dg.name}</option>)}
                        </select>
                    </div>

                    <div className="form-group mb-0">
                        <label className="text-xs font-semibold uppercase tracking-wider mb-2 block pl-1" style={{ color: 'var(--text-secondary)' }}>Rampa</label>
                        <select
                            className="input w-full"
                            value={selection.dockId}
                            disabled={!selection.doorGroupId}
                            onChange={e => setSelection({ ...selection, dockId: e.target.value, selectedSlotTime: null })}
                        >
                            <option value="">Seçiniz</option>
                            {filteredDocks.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                    </div>
                </div>

                {/* 2. Slot Selection Area */}
                {selection.dockId ? (
                    <div className="flex-1 flex flex-col gap-sm mt-sm">

                        {/* Toolbar: Filter & Pagination Info */}
                        <div className="flex justify-between items-center px-sm">
                            <div className="flex items-center gap-sm bg-surface rounded-lg p-1 border border-border">
                                <Button
                                    variant="ghost"
                                    className="px-2 py-1 h-8 text-secondary hover:text-primary"
                                    onClick={() => {
                                        const d = new Date(selectedDate);
                                        d.setDate(d.getDate() - 1);
                                        onDateChange(d.toISOString().split('T')[0]);
                                    }}
                                >
                                    ◀
                                </Button>
                                <div className="relative">
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => onDateChange(e.target.value)}
                                        className="absolute inset-0 w-full h-full cursor-pointer"
                                        style={{ opacity: 0, zIndex: 10 }}
                                    />
                                    <span className="font-bold text-lg px-2 cursor-pointer flex items-center justify-center h-full select-none">
                                        {new Date(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </span>
                                </div>
                                <Button
                                    variant="ghost"
                                    className="px-2 py-1 h-8 text-secondary hover:text-primary"
                                    onClick={() => {
                                        const d = new Date(selectedDate);
                                        d.setDate(d.getDate() + 1);
                                        onDateChange(d.toISOString().split('T')[0]);
                                    }}
                                >
                                    ▶
                                </Button>
                            </div>

                            <div className="flex items-center gap-md">
                                <label className="flex items-center gap-xs cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={filterAvailable}
                                        onChange={e => {
                                            setFilterAvailable(e.target.checked);
                                            setCurrentPage(0); // Reset page on filter change
                                        }}
                                        className="form-checkbox rounded text-primary"
                                    />
                                    <span className="text-xs font-medium text-secondary">Sadece Boşları Göster</span>
                                </label>
                            </div>
                        </div>

                        {/* Grid */}
                        {slots.length > 0 ? (
                            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3 p-xs">
                                {currentSlots.map((slot) => {
                                    const isSelected = selection.selectedSlotTime === slot.timeLabel;
                                    return (
                                        <button
                                            key={slot.timeLabel}
                                            onClick={() => handleSlotClick(slot)}
                                            className="btn-slot font-mono text-lg font-bold transition-all duration-200 border rounded-lg flex flex-col items-center justify-center relative"
                                            style={{
                                                minHeight: '60px',
                                                backgroundColor: isSelected
                                                    ? 'var(--primary-color)'
                                                    : slot.isOccupied
                                                        ? 'rgba(239, 68, 68, 0.15)' // Red tint
                                                        : 'rgba(16, 185, 129, 0.15)', // Green tint
                                                borderColor: isSelected
                                                    ? 'var(--primary-color)'
                                                    : slot.isOccupied
                                                        ? 'rgba(239, 68, 68, 0.5)'
                                                        : 'rgba(16, 185, 129, 0.5)',
                                                color: isSelected
                                                    ? '#ffffff'
                                                    : slot.isOccupied
                                                        ? 'rgba(239, 68, 68, 0.9)'
                                                        : 'rgba(16, 185, 129, 0.9)',
                                                cursor: 'pointer',
                                                transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                                                opacity: 1
                                            }}
                                        >
                                            {slot.timeLabel}
                                            {/* Status Dot */}
                                            {!slot.isOccupied && !isSelected && (
                                                <span
                                                    className="absolute bottom-1 w-1.5 h-1.5 rounded-full"
                                                    style={{ backgroundColor: 'rgb(16, 185, 129)', boxShadow: '0 0 8px rgba(16,185,129,0.8)' }}
                                                ></span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-xl opacity-50">
                                {duration <= 0
                                    ? "Lütfen önce yük bilgilerini (hacim/ağırlık) giriniz."
                                    : "Uygun slot bulunamadı."}
                            </div>
                        )}

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center gap-md mt-auto pt-sm">
                                <Button
                                    variant="secondary"
                                    className="btn-sm"
                                    disabled={currentPage === 0}
                                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                >
                                    ← Önceki
                                </Button>
                                <span className="text-xs font-mono opacity-70">
                                    Sayfa {currentPage + 1} / {totalPages}
                                </span
                                >
                                <Button
                                    variant="secondary"
                                    className="btn-sm"
                                    disabled={currentPage >= totalPages - 1}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                >
                                    Sonraki →
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center opacity-30 text-center p-xl">
                        <div className="text-4xl mb-md">🏗️</div>
                        <p>Lütfen önce bir rampa seçiniz.</p>
                    </div>
                )}
            </div>

            {/* Footer Actions */}
            <div className="flex justify-between items-center mt-lg pt-md border-t border-border">
                <div className="text-xs text-secondary">
                    {selection.selectedSlotTime
                        ? <span className="text-primary font-bold">Seçilen: {selection.selectedSlotTime}</span>
                        : "Henüz seçim yapılmadı"}
                </div>
                <div className="flex gap-md">
                    <Button variant="secondary" onClick={onClose}>İptal</Button>
                    <Button
                        variant="primary"
                        onClick={handleSelect}
                        disabled={!selection.dockId || !selection.selectedSlotTime}
                        className="shadow-lg shadow-primary/20"
                    >
                        Seçimi Onayla
                    </Button>
                </div>
            </div>

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                onClose={closeConfirm}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
                cancelText={confirmConfig.cancelText}
            />
        </Modal>
    );
};

export default ManualSlotSelectionModal;
